// ─── Tipos mínimos necesarios para imprimir ───────────────────────────────────

export interface PrintItem {
  quantity: number
  dishName: string
  optionName: string
  price: number
  notes?: string | null
  guestName?: string | null
  customizations?: { included: boolean; name: string; state?: string | null }[]
}

export interface PrintOrder {
  id: string
  orderNumber?: number | null
  customerName?: string | null
  tableNumber: number
  tableType: 'DINE_IN' | 'TAKEOUT'
  serviceTiming: string
  status: string
  createdAt: string
  items: PrintItem[]
  subtotal: number
  total: number
  tipPercent?: number | null
  tipAmount?: number
  loyaltyDiscount?: number
  restaurantName: string
  restaurantAddress?: string | null
  razonSocial?: string | null
  rfc?: string | null
  regimenFiscal?: string | null
  direccionFiscal?: string | null
  urlFacturacion?: string | null
}

// ─── Helper interno ────────────────────────────────────────────────────────────

function formatCurrency(n: number) {
  return `$${n.toFixed(2)}`
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
}

const TIMING_LABEL: Record<string, string> = {
  AS_THEY_COME: 'Conforme vayan saliendo',
  ALL_TOGETHER: 'Todo junto',
  AT_THE_END:   'Al final',
}

// ─── Estilos base del ticket ───────────────────────────────────────────────────

const BASE_STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Courier New', monospace; font-size: 12px; width: 80mm; padding: 8px; color: #000; }
  .center { text-align: center; }
  .bold { font-weight: bold; }
  .large { font-size: 16px; }
  .xlarge { font-size: 20px; }
  .divider { border-top: 1px dashed #000; margin: 6px 0; }
  .row { display: flex; justify-content: space-between; gap: 4px; }
  .row .right { text-align: right; white-space: nowrap; }
  .indent { padding-left: 12px; color: #444; }
  .total-row { font-weight: bold; font-size: 14px; }
  .mt4 { margin-top: 4px; }
  .mt8 { margin-top: 8px; }
  @media print {
    @page { margin: 0; size: 80mm auto; }
    body { width: 80mm; }
  }
`

// ─── Ticket de cocina (se imprime al confirmar la orden) ───────────────────────

export function buildKitchenTicket(order: PrintOrder): string {
  const itemsHtml = order.items.map((item) => {
    const customLines = (item.customizations ?? [])
      .map((c) => {
        if (!c.included) return `<div class="indent">✗ Sin ${c.name}</div>`
        if (c.state)     return `<div class="indent">• ${c.name}: ${c.state}</div>`
        return ''
      })
      .filter(Boolean)
      .join('')

    return `
      <div class="mt4">
        <div class="row bold">
          <span>${item.quantity}x ${item.dishName}${item.optionName !== 'Estándar' ? ` (${item.optionName})` : ''}</span>
        </div>
        ${item.guestName ? `<div class="indent">Para: ${item.guestName}</div>` : ''}
        ${item.notes ? `<div class="indent">Nota: ${item.notes}</div>` : ''}
        ${customLines}
      </div>`
  }).join('')

  return `
    <!DOCTYPE html><html><head>
    <meta charset="utf-8">
    <style>${BASE_STYLES}</style>
    </head><body>
      <div class="center bold xlarge">COCINA</div>
      <div class="center bold large">Mesa ${order.tableNumber}${order.tableType === 'TAKEOUT' ? ' · PARA LLEVAR' : ''}</div>
      ${order.customerName ? `<div class="center">${order.customerName}${order.orderNumber ? ` · #${order.orderNumber}` : ''}</div>` : ''}
      <div class="center">${formatTime(order.createdAt)}</div>
      <div class="center">${TIMING_LABEL[order.serviceTiming] ?? order.serviceTiming}</div>
      <div class="divider"></div>
      ${itemsHtml}
      <div class="divider"></div>
      <div class="center">${order.items.length} platillo(s)</div>
    </body></html>`
}

// ─── Ticket de cliente (se imprime al cobrar) ──────────────────────────────────

export function buildReceiptTicket(order: PrintOrder): string {
  const itemsHtml = order.items.map((item) => `
    <div class="row mt4">
      <span>${item.quantity}x ${item.dishName}${item.optionName !== 'Estándar' ? ` (${item.optionName})` : ''}</span>
      <span class="right">${formatCurrency(item.price * item.quantity)}</span>
    </div>
  `).join('')

  const tip = order.tipAmount ?? (order.subtotal * ((order.tipPercent ?? 0) / 100))
  const discount = order.loyaltyDiscount ?? 0
  const netSubtotal = order.subtotal - discount
  const netTip = netSubtotal * ((order.tipPercent ?? 0) / 100)
  const netTotal = netSubtotal + netTip

  return `
    <!DOCTYPE html><html><head>
    <meta charset="utf-8">
    <style>${BASE_STYLES}</style>
    </head><body>
      <div class="center bold xlarge">${order.restaurantName}</div>
      ${order.restaurantAddress ? `<div class="center">${order.restaurantAddress}</div>` : ''}
      ${order.razonSocial     ? `<div class="center">${order.razonSocial}</div>`     : ''}
      ${order.rfc             ? `<div class="center">RFC: ${order.rfc}</div>`        : ''}
      ${order.regimenFiscal   ? `<div class="center">${order.regimenFiscal}</div>`   : ''}
      ${order.direccionFiscal ? `<div class="center">${order.direccionFiscal}</div>` : ''}
      <div class="divider"></div>
      <div class="center">Mesa ${order.tableNumber}${order.tableType === 'TAKEOUT' ? ' · PARA LLEVAR' : ''}</div>
      ${order.customerName ? `<div class="center">${order.customerName}${order.orderNumber ? ` · Orden #${order.orderNumber}` : ''}</div>` : ''}
      <div class="center">${new Date(order.createdAt).toLocaleDateString('es-MX')} ${formatTime(order.createdAt)}</div>
      <div class="divider"></div>
      ${itemsHtml}
      <div class="divider"></div>
      <div class="row"><span>Subtotal</span><span class="right">${formatCurrency(order.subtotal)}</span></div>
      ${discount > 0 ? `<div class="row"><span>Descuento lealtad</span><span class="right">-${formatCurrency(discount)}</span></div>` : ''}
      ${discount > 0 ? `<div class="row"><span>Subtotal neto</span><span class="right">${formatCurrency(netSubtotal)}</span></div>` : ''}
      ${(order.tipPercent ?? 0) > 0 ? `<div class="row"><span>Propina (${order.tipPercent}%)</span><span class="right">${formatCurrency(discount > 0 ? netTip : tip)}</span></div>` : ''}
      <div class="divider"></div>
      <div class="row total-row"><span>TOTAL</span><span class="right">${formatCurrency(discount > 0 ? netTotal : order.total)}</span></div>
      <div class="divider"></div>
      <div class="center mt8">¡Gracias por su visita!</div>
      ${order.urlFacturacion ? `<div class="divider"></div><div class="center mt4">Para facturación diríjase a:</div><div class="center bold">${order.urlFacturacion}</div>` : ''}
    </body></html>`
}

// ─── Función principal de impresión ───────────────────────────────────────────

export function printTicket(html: string) {
  const win = window.open('', '_blank', 'width=400,height=600')
  if (!win) return
  win.document.write(html)
  win.document.close()
  win.focus()
  // Esperar a que cargue antes de imprimir
  win.onload = () => {
    win.print()
    win.close()
  }
  // Fallback si onload no se dispara
  setTimeout(() => {
    try { win.print(); win.close() } catch { /* ya se cerró */ }
  }, 800)
}
